/// <reference types="../../../../env.d.ts" />

import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterOutlet, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import {
  AppStore,
  FirebaseService,
  StorageService,
  DataService,
  Session,
  Settings,
  normalizeDate,
  ARSettings
} from '@kanek/shared-data-access';
import { ArService } from '@kanektech.com/game-engine';
import { LoadingComponent, AudioService } from '@kanek/shared-ui';
import { LanguageInitializationService } from './services/language-initialization.service';
import { LanguageSelectorService } from './services/language-selector.service';
import { CampaignFlowService } from './services/campaign-flow.service';
import { navigateToPrizeDestination } from './app.routes';

/**
 * Interface for campaign short code data structure
 */
interface ShortCodeData {
  id: string;
  name: string;
  gameType?: string;
  arSettings?: ARSettings;
  gameSettings?: Record<string, unknown>;
}

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    LoadingComponent
  ],
  template: `
    <main>
      <router-outlet />
    </main>

    <lib-loading [loading]="appStore.loading() && !isErrorPage" />
  `,
  styles: `
    main {
      height: 100%;
    }
  `
})
export class AppComponent implements OnInit, OnDestroy  {
  protected appStore = inject(AppStore);
  private router = inject(Router);
  private storage = inject(StorageService);
  private dataService = inject(DataService);
  private firebaseService = inject(FirebaseService);
  private arService = inject(ArService);
  private audioService = inject(AudioService);
  private languageService = inject(LanguageInitializationService);
  private languageSelectorService = inject(LanguageSelectorService);
  private campaignFlowService = inject(CampaignFlowService);

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  // Track if we're on the error page to avoid initialization
  isErrorPage = false;

  private sessionId: string | null = null;
  private short: string | null = null;
  private clientId: string | null = null;
  private initStarted = false;
  // Stored so init() can await it — ensures client default language is set before
  // session locale restore runs. Guards against the race where the CF session load
  // resolves faster than the Firestore client-locale DB call in initializeLanguage().
  private languageInitPromise: Promise<void> = Promise.resolve();

  constructor(
    private route: ActivatedRoute
  ) {
    // Initialize client configuration from environment variables
    this.clientId = import.meta.env['_NGX_ENV_CLIENT_ID'] || null;

    if (!this.clientId) {
      console.error('❌ CLIENT_ID not found in environment variables. Please ensure _NGX_ENV_CLIENT_ID is set.');
      throw new Error('CLIENT_ID is required but not found in environment configuration');
    }

    console.log('🏗️ Client configuration:', {
      clientId: this.clientId,
      source: 'environment'
    });
  }

  ngOnInit(): void {
    const appVersion = import.meta.env['_NGX_ENV_APP_VERSION'] || 'dev';
    console.warn(`[Experience] v${appVersion}`);

    // Initialize language FIRST before any rendering — store the promise so init() can await it
    this.languageInitPromise = this.initializeLanguage();

    this.router.events.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(event => {
      // Perform actions that require query parameters here
      if (event instanceof NavigationEnd) {
        // Check if we're on the error page
        this.isErrorPage = event.url.includes('/campaign-error');

        // If on error page, ensure loading is stopped
        if (this.isErrorPage) {
          this.appStore.stopLoading();
        }

        // Apply AR bypass check on every navigation
        this.arService.applyGlobalBypass();

        // Only initialize once — initStarted prevents re-entry from subsequent NavigationEnd
        // events triggered by internal navigations (param clearing, session restore redirects)
        if (!this.appStore.ready() && !this.isErrorPage && !this.initStarted) {
          const queryParams = this.route.snapshot.queryParamMap;

          if (queryParams.has('s')) {
            this.short = queryParams.get('s');
          }

          if (queryParams.has('i')) {
            this.sessionId = queryParams.get('i');
          }

          this.init();
        }
      }
    });
  }

  ngOnDestroy(): void {
    // Unsubscribe to prevent memory leaks
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Initialize language before any template rendering to prevent ExpressionChangedAfterItHasBeenCheckedError
   */
  private async initializeLanguage(): Promise<void> {
    try {
      console.log('🌐 Initializing language support...');
      await this.languageService.initializeLanguage();
      console.log('✅ Language initialization complete');
    } catch (error) {
      console.error('❌ Language initialization failed:', error);
      // Continue with default language if initialization fails
    }
  }

  async init(): Promise<void> {
    this.initStarted = true;
    this.appStore.startLoading();

    // Wait for language initialization to complete before loading the session.
    // This guarantees initializeLanguage() (client default) always finishes before
    // the session.locale restore below — preventing the client default from overwriting
    // the user's saved preference when the Firestore DB call resolves late.
    await this.languageInitPromise;

    await this.storage.init();

    // Parallel storage reads: stored short code + legacy sessionId for one-time migration
    const [storedShort, legacySessionId] = await Promise.all([
      this.storage.get('short') as Promise<string | null>,
      this.storage.get('sessionId') as Promise<string | null>,
    ]);

    // One-time migration: 'sessionId' (single global key) → 'session_{shortCode}' (per-short-code key).
    // Prevents existing users from losing their session on first load of the new code.
    if (legacySessionId) {
      const migrateToShort = storedShort || this.short;
      if (migrateToShort) {
        // setSync writes to localStorage (immediately visible) AND async storage
        this.storage.setSync(`session_${migrateToShort}`, legacySessionId);
      }
      await this.storage.remove('sessionId');
      console.log('🔄 Migrated legacy sessionId to per-short-code storage key');
    }

    // Determine final short code (URL param takes priority over stored value)
    const shortCode = this.short || storedShort;
    if (!shortCode) {
      console.error('No short code available');
      this.router.navigate(['/campaign-error'], {
        queryParams: {
          status: 'missing_short_code',
          message: 'No campaign code was provided in the link.'
        }
      });
      return;
    }

    // Store for synchronous guard access (validateCampaign guard reads this key)
    this.storage.setSync('short', shortCode);

    // Determine session ID for THIS specific short code.
    // Priority: URL ?i= param (server-issued recovery link) > stored session > null (new session)
    // getSync reads from localStorage — consistent with how we write via setSync below
    const storedSessionId = this.storage.getSync(`session_${shortCode}`);
    const sessionId: string | null = this.sessionId ?? storedSessionId;

    // 🚀 OPTIMIZATION: Both Cloud Functions only need shortCode — fire them in parallel.
    // getSession creates/retrieves the session; getSettings loads the full campaign config.
    // This cuts sequential CF round-trips (~400ms) to a single parallel wait (~200ms).
    const [sessionResult, settingsResult] = await Promise.all([
      this.dataService.getSession(sessionId, shortCode, navigator.userAgent),
      this.dataService.getSettings(shortCode, this.clientId ?? undefined),
    ]);

    const session = sessionResult.data as Session;
    const campaignData = settingsResult.data as Record<string, unknown>;
    console.log('Session loaded:', { id: session.id, short: session.short, prizeWon: session.prizeWon });

    // Persist session under the per-short-code key; keep 'short' key updated for guard access.
    // setSync writes to localStorage (immediately visible in devtools) AND to Ionic async storage.
    const finalShort = session.short ?? shortCode;
    this.storage.setSync(`session_${finalShort}`, session.id);
    this.storage.setSync('short', finalShort);

    // prizeRevealed is a client-side-only animation flag — never stored server-side, always
    // false on cold load. Store the session as-is so that sessions with prizeWon but
    // prizeRevealed=false stay on the play page and allow replay with the same predetermined
    // prize (required for scratchcard/gameboard which determine the prize before user interaction).
    this.appStore.setSession(session);
    this.short = finalShort;

    // Determine effective locale: user's saved preference → client default from initializeLanguage().
    // Always call switchLanguage() via LanguageSelectorService (not the init service) so the locale
    // is written to the session document. Without this stamp, the messaging CF has no locale to read,
    // causing confirmation emails/SMS to fall back to English even when the client default is French.
    const effectiveLocale = session.locale ?? this.languageService.getCurrentLocale();
    await this.languageSelectorService.switchLanguage(effectiveLocale);

    // 🔒 SECURITY: Validate that the campaign belongs to the configured client
    const campaignClientId = campaignData['clientId'] as string;
    if (this.clientId && campaignClientId && this.clientId !== campaignClientId) {
      console.error(`🚨 SECURITY VIOLATION: Campaign "${this.short}" belongs to client "${campaignClientId}" but this app is configured for client "${this.clientId}". Cross-client access denied.`);
      this.router.navigate(['/campaign-error'], {
        queryParams: {
          error: 'client_mismatch',
          shortCode: this.short
        }
      });
      return;
    }

    // Initialize campaign flow for dynamic step content
    this.campaignFlowService.initializeFlow(campaignData);

    const matchingShortCode = this.findShortCode(campaignData, this.short);
    if (!matchingShortCode) {
      console.error('❌ Matching short code not found');
      this.router.navigate(['/campaign-error'], {
        queryParams: {
          status: 'short_code_not_found',
          message: 'Short code configuration not found.',
          shortCode: this.short
        }
      });
      return;
    }

    const gameType = matchingShortCode.gameType || 'wheel';
    const gameSettings = matchingShortCode.gameSettings;
    if (!gameSettings) {
      console.error('❌ Game settings not found in short code');
      this.router.navigate(['/campaign-error'], {
        queryParams: {
          status: 'settings_missing',
          message: 'Game configuration is missing.',
          shortCode: this.short
        }
      });
      return;
    }

    const settings: Settings = {
      experience: {
        type: gameType,
        settings: gameSettings,
        ...(campaignData['experience'] as object)
      },
      ...(campaignData['settings'] as object)
    };

    // Load AR pattern data only if this short code requires AR
    try {
      const arSettings = matchingShortCode.arSettings;
      const requiresAR = arSettings != null && (arSettings.enabled ?? true);

      if (requiresAR && arSettings) {
        console.log('🎯 Short code requires AR — loading AR pattern and configuration');
        const patternData = this.extractARPatternFromCampaign(campaignData, this.short);
        settings.patternUrl = this.createPatternDataUrl(patternData);
        if (arSettings.patternRatio !== undefined) settings.patternRatio = arSettings.patternRatio;
        if (arSettings.smoothCount !== undefined) settings.smoothCount = arSettings.smoothCount;
        if (arSettings.smoothTolerance !== undefined) settings.smoothTolerance = arSettings.smoothTolerance;
        if (arSettings.smoothThreshold !== undefined) settings.smoothThreshold = arSettings.smoothThreshold;
        if (arSettings.size !== undefined) settings.size = arSettings.size;
        if (arSettings.sceneScale !== undefined) settings.sceneScale = arSettings.sceneScale;
        console.log('✅ AR configuration loaded:', {
          patternRatio: settings.patternRatio, size: settings.size, sceneScale: settings.sceneScale
        });
      } else {
        console.log('ℹ️ Short code does not require AR — launching direct game experience');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ Failed to initialize campaign:', errorMessage);
      this.router.navigate(['/campaign-error'], {
        queryParams: {
          status: 'initialization_failed',
          message: errorMessage,
          shortCode: this.short
        }
      });
      return;
    }

    this.appStore.setCampaign({
      ...settings,
      prizes: campaignData['prizes']
    });
    this.configureAudioService(campaignData, gameSettings);

    // Clear URL params on restore. If prizeRevealed was persisted to localStorage
    // for this session (set by play.component when the prize celebration completed),
    // navigate the user directly to the win/redeem destination so they are returned
    // to where they left off rather than back to the play page.
    const prizeRevealedKey = `prizeRevealed_${session.id}`;
    if (session.prizeWon && this.storage.getSync(prizeRevealedKey) === 'true') {
      navigateToPrizeDestination(session, this.router);
    } else {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {},
        replaceUrl: true
      });
    }

    this.appStore.stopLoading();
    this.appStore.setReady();
  }

  /**
   * Find a short code in the campaign data
   * @param campaignData Campaign data from Firestore
   * @param shortCodeId Short code ID to find
   * @returns Matching short code or null if not found
   */
  private findShortCode(campaignData: Record<string, unknown>, shortCodeId: string): ShortCodeData | null {
    const shortCodes = campaignData['shortCodes'] as ShortCodeData[] | undefined;

    if (!shortCodes || shortCodes.length === 0) {
      console.error('❌ No short codes found in campaign data');
      return null;
    }

    const matchingShortCode = shortCodes.find(sc => sc.id === shortCodeId);

    if (!matchingShortCode) {
      console.error(`❌ Short code "${shortCodeId}" not found in campaign`);
      return null;
    }

    return matchingShortCode;
  }

  /**
   * Extract AR pattern data from campaign short code settings
   * @param campaignData Campaign data from Firestore
   * @param shortCodeId Short code ID to find
   * @returns AR pattern data string
   * @throws Error if pattern data not found or short code doesn't have AR enabled
   */
  private extractARPatternFromCampaign(campaignData: Record<string, unknown>, shortCodeId: string): string {
    const matchingShortCode = this.findShortCode(campaignData, shortCodeId);

    if (!matchingShortCode) {
      throw new Error(`Short code "${shortCodeId}" not found in campaign short codes`);
    }

    // This method should only be called for AR-enabled short codes
    if (!matchingShortCode.arSettings) {
      throw new Error(`Short code "${shortCodeId}" does not have AR enabled`);
    }

    const patternData = matchingShortCode.arSettings.patternData;

    if (!patternData) {
      throw new Error(`AR pattern data not found for AR-enabled short code "${shortCodeId}". Please regenerate the campaign.`);
    }

    // Log pattern metadata for debugging
    const { patternVersion, patternGeneratedAt } = matchingShortCode.arSettings;
    console.log('📦 AR Pattern loaded:', {
      shortCode: shortCodeId,
      size: `${patternData.length} chars`,
      version: patternVersion || 'N/A',
      generatedAt: patternGeneratedAt ? normalizeDate(patternGeneratedAt).toISOString() : 'Unknown'
    });

    return patternData;
  }

  /**
   * Convert AR pattern data string to data URL for AR.js
   * @param patternData The .patt file content
   * @returns Data URL with encoded pattern data
   */
  private createPatternDataUrl(patternData: string): string {
    // AR.js expects a URL it can fetch from
    // Data URL embeds the pattern directly, avoiding network requests
    return `data:text/plain;charset=utf-8,${encodeURIComponent(patternData)}`;
  }

  /**
   * Configure audio service based on campaign and short code settings
   * @param campaignData Campaign data from Firestore
   * @param shortCodeSettings Short code game settings
   */
  private configureAudioService(campaignData: Record<string, unknown>, shortCodeSettings: Record<string, unknown>): void {
    const campaignEnableSFX = campaignData['enableSoundEffects'] !== false;
    const shortCodeEnableSFX = (shortCodeSettings as any).enableSoundEffects !== false;
    const audioEnabled = campaignEnableSFX && shortCodeEnableSFX;
    this.audioService.setEnabled(audioEnabled);
  }
}
